import {
  Details,
  Link as ExternalLink,
  Heading,
  Paragraph,
  Tabs,
  Tooltip,
} from "@digdir/designsystemet-react";
import { ExternalLinkIcon, LinkIcon } from "@navikt/aksel-icons";
import { Link } from "@tanstack/react-router";
import { type MouseEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import EksporterExcel from "@/assets/veiledning/eksporter-csv-excel.png?inline";
import EksporterLibreOffice1 from "@/assets/veiledning/eksporter-csv-libre-1.png?inline";
import EksporterLibreOffice2 from "@/assets/veiledning/eksporter-csv-libre-2.png?inline";
import EksporterMicrosoft365 from "@/assets/veiledning/eksporter-csv-microsoft-365.png?inline";
import EksporterNumbers from "@/assets/veiledning/eksporter-csv-numbers.png?inline";
import ImportCsvExcelWindows from "@/assets/veiledning/importer-csv-excel.png?inline";
import ImportCsvExcelMac1 from "@/assets/veiledning/importer-csv-excel-mac-1.png?inline";
import ImportCsvExcelMac2 from "@/assets/veiledning/importer-csv-excel-mac-2.png?inline";
import ForhåndsvisningCsv from "@/assets/veiledning/preview-csv.png?inline";
import RareVerdierImg from "@/assets/veiledning/rare-verdier.png?inline";
import { Page } from "@/components";
import { MaanedsplanFrister } from "../shared/MaanedsplanFrister";
import { BeregneMvaSection } from "./BeregneMvaSection";

export const VeiledningPage = () => {
  const { t } = useTranslation();

  return (
    <main className="container mx-auto grid grid-cols-1 xl:grid-cols-[1fr_auto] py-14 gap-6 gap-x-12 max-w-page">
      <Page withBackLink>
        <div className="flex flex-col xl:flex-row gap-12">
          <div className="flex flex-col gap-10 max-w-4xl! mx-0 w-full">
            <Heading level={1} data-size="xl">
              {t("pages.veiledning.tittel")}
            </Heading>
            <Paragraph>{t("pages.veiledning.beskrivelse")}</Paragraph>

            <section>
              <Heading level={2} data-size="xs" className="mb-5">
                Avtaler
              </Heading>

              <InfoContainer
                title={"Hvordan eksportere avtaler"}
                content={
                  <section className="space-y-9">
                    <div>
                      <Paragraph>
                        For alle selskaper som ikke har egen løsning for
                        håndtering av Norgespris avtaler, finner du nye
                        bestillinger, avbestillinger og eventuelle endringer
                        gjort for en avtale på{" "}
                        <Link
                          target="_blank"
                          rel="noreferrer"
                          to={"/bestillinger"}
                          data-size={"sm"}
                        >
                          Bestillinger
                        </Link>{" "}
                        . Disse er knyttet til en <i>Avtaleperiode</i> som det
                        må innrapporteres målerdata for.
                      </Paragraph>
                    </div>
                    <div>
                      <Heading level={3} data-size="xs">
                        {"Hva skal du gjøre videre?"}
                      </Heading>
                      <Paragraph>
                        Last ned eksporten av Norgespris avtaler for å se alle
                        avtalene med kundenummer, måler-ID, boligtype m.m. ved
                        hjelp av et regnearkprogram som Excel, Numbers eller
                        LibreOffice.
                      </Paragraph>
                    </div>
                  </section>
                }
              />

              <InfoContainer
                title={"Ingen avtaler"}
                content={
                  <Paragraph>
                    På bestillingoversikten finner du bare avtaler gjort via{" "}
                    <ExternalLink
                      target="_blank"
                      rel="noreferrer"
                      href={"https://norgespris-fjernvarme.no/"}
                    >
                      Norgespris for fjernvarme
                    </ExternalLink>
                    . Dersom dere har et eget system for håndtering av avtaler,
                    må dere bruke dette for å finne deres avtaler. Alternativt
                    er det ingen som har meldt seg opp enda.
                  </Paragraph>
                }
              />
            </section>

            <section>
              <Heading level={2} data-size="xs" className="mb-5">
                Ofte stilte spørsmål
              </Heading>
              <InfoContainer
                title={
                  "Separate kontoer og prosjektregnskap for Norgespris og strømstøtte"
                }
                content={
                  <section className="space-y-3">
                    <Paragraph>
                      I lov om Norgespris og strømstønad til husholdninger står
                      det:
                    </Paragraph>
                    <Paragraph className="italic">
                      <ExternalLink
                        href="https://lovdata.no/dokument/NL/lov/2025-06-20-44/KAPITTEL_4#%C2%A721"
                        target="_blank"
                        rel="noreferrer"
                      >
                        § 21 Krav til regnskapsmessig skille{" "}
                        <ExternalLinkIcon fontSize={"1.5rem"} />
                      </ExternalLink>
                    </Paragraph>
                    <Paragraph className="italic">
                      Fjernvarmeselskapene plikter å føre egne prosjektregnskap
                      og separate kontoer for administrasjonen av ordning for
                      sikring av fjernvarmepris og strømstønadsordningen.
                      Fjernvarmeselskapene skal årlig fremlegge bekreftelse fra
                      revisor på at det er ført separate prosjektregnskap, og at
                      det er benyttet separate kontoer.
                    </Paragraph>
                    <Paragraph>
                      NVE har fått flere spørsmål om hva som menes med kontoer.
                      Her betyr kontoer at det skal opprettes og benyttes egne
                      bankkontoer for administrasjonen av Norgespris og
                      strømstønad. Dette gjelder mellom fjernvarmeleverandørene
                      og Enova. Dette innebærer at det opprettes en egen
                      bankkonto for Norgespris der refusjon fra Enova for
                      utbetalinger av prissikringsbeløp innbetales, og en annen
                      bankkonto for strømstønad.
                    </Paragraph>
                    <Paragraph>
                      Bankkontoene som fjernvarmeleverandøren oppgir for å motta
                      refusjon fra Enova på, må være knyttet til selskapet som
                      kunden har inngått Norgespris med eller som kunden skal
                      motta utbetalinger av strømstønad fra. Dette for at Enova
                      skal kunne sjekke opp kontoene i{" "}
                      <ExternalLink
                        href="https://www.sparebank1.no/nb/bank/bedrift/daglig-drift/nettbank-bedrift/kar-registeret.html"
                        target="_blank"
                        rel="noreferrer"
                      >
                        konto- og adresseregisteret (KAR){" "}
                        <ExternalLinkIcon fontSize={"1.5rem"} />
                      </ExternalLink>
                      .
                    </Paragraph>
                    <Paragraph>
                      I tillegg skal det føres et eget prosjektregnskap for
                      ordningen. Dette vil synliggjøre hvordan støtten er
                      fordelt mellom Enova og fjernvarmekundene.
                    </Paragraph>
                    <Paragraph>
                      Disse postene skal inngå i et slikt prosjektregnskap:
                    </Paragraph>
                    <ul className="list-none pl-5">
                      <li>Xxxx - Utbetaling til kunde for strømstønad</li>
                      <li>
                        Xxxx - Refusjon fra Enova for utbetaling til kunde for
                        strømstønad
                      </li>
                      <li>Xxxx - Utbetaling til kunde for Norgespris</li>
                      <li>
                        Xxxx - Refusjon fra Enova for utbetaling til kunde for
                        Norgespris
                      </li>
                      <li>Xxxx - Innbetaling fra kunde for Norgespris</li>
                      <li>
                        Xxxx - Overføring av innbetaling for Norgespris til
                        Enova
                      </li>
                    </ul>
                  </section>
                }
              />

              <InfoContainer
                title={"Frister for innrapportering av målerdata"}
                content={
                  <section className="space-y-3">
                    <Paragraph>
                      Enova kjører et oppgjør for Norgespris og strømstønad
                      etter den 8. virkedag hver måned. Etter dette må man vente
                      til påfølgende måned for å rapportere inn målerdata.
                    </Paragraph>

                    <MaanedsplanFrister />
                  </section>
                }
              />

              <InfoContainer
                title={
                  "Separate innbetalinger og utbetalinger fra 1. september 2026"
                }
                content={
                  <section className="space-y-3">
                    <Paragraph>
                      Tidligere har innbetalinger og utbetalinger blitt avregnet
                      mot hverandre i oppgjøret, slik at det for hver måned bare
                      har blitt enten et innbetalingskrav eller en utbetaling.
                    </Paragraph>
                    <Paragraph>
                      Fra og med 1. september 2026 håndteres hver utbetaling og
                      hvert innbetalingskrav som en egen transaksjon, både for
                      Norgespris og for strømstønad. Det betyr at dere kan få
                      flere transaksjoner for samme oppgjør: har dere for
                      eksempel en korrigering fra en tidligere måned, kan dere
                      få et innbetalingskrav for det beløpet og samtidig få
                      utbetalt stønaden.
                    </Paragraph>
                    <Paragraph>
                      Et innbetalingskrav kan generelt skyldes:
                    </Paragraph>
                    <ul className="list-disc pl-5">
                      <li>
                        korrigeringer av tidligere innsendte forbruksdata, eller
                      </li>
                      <li>
                        at den gjennomsnittlige elspotprisen i den aktuelle
                        perioden har vært lavere enn den myndighetsfastsatte
                        Norgesprisen.
                      </li>
                    </ul>
                    <Paragraph>
                      Endring i praksis vil gi økt kontroll over utbetaling av
                      støtte og dermed i større grad legge til rette for
                      korrekte stønadsbeløp.
                    </Paragraph>
                  </section>
                }
              />

              <InfoContainer
                title={"Hvilke spotpriser skal brukes?"}
                content={
                  <section className="space-y-3">
                    <Paragraph>
                      Månedsspotprisene er hentet fra Epex Spot. For at
                      utregningene skal bli mest korrekte bes alle å bruke
                      spotprisene fra{" "}
                      <ExternalLink
                        href="https://www.nve.no/energi/energisystem/fjernvarme/norgespris-og-stroemstoettesats/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        NVE - Norgespris og strømstøttesats
                      </ExternalLink>
                      . Hvis deres tall er basert på Norpool spotpriser så
                      støttes det og.
                    </Paragraph>
                  </section>
                }
              />

              <InfoContainer
                title={"Hvordan beregnes mva?"}
                content={<BeregneMvaSection />}
              />
            </section>

            <section>
              <Heading level={2} data-size="xs" className="mb-5">
                Målerdata innrapportering
              </Heading>

              <InfoContainer
                title={"Registrer målerdata"}
                content={
                  <section className="space-y-3">
                    <Paragraph>
                      For å få utbetalt støtte i forbindelse med Norgespris- og
                      strømstøtteordningene så man rapportere inn månedsforbruk
                      og støttebeløp per kundeforhold. Det gjøres ved å laste
                      opp et regneark etter månedsslutt på inne på{" "}
                      <Link to="/maalerdata" target="_blank" rel="noreferrer">
                        Målerdata
                      </Link>
                      -siden.
                    </Paragraph>

                    <Paragraph>
                      Neste periode vil først bli tilgjenglig når snittprisen
                      for forrige måned blir publisert av NVE.
                    </Paragraph>

                    <Paragraph>
                      <Link to="/dokumentasjon/innrapportering">
                        Last ned måledata-mal for innrapportering av målerdata
                      </Link>
                    </Paragraph>

                    <Paragraph>
                      PS! De fleste program for regneark vil kunne åpne
                      måledata-mal, men i noen tilfeller kan formatering endre
                      seg. Kontroller derfor data, slik at de blir korrekt.
                    </Paragraph>

                    <Paragraph>
                      <Link to="/dokumentasjon/innrapportering">
                        Les mer om filspesifikasjon for målerdata
                      </Link>
                    </Paragraph>
                  </section>
                }
              />

              <InfoContainer
                title={"Hvordan lage målerdata-fil"}
                content={
                  <section className="space-y-3">
                    <Paragraph>
                      Laste ned malen for målerdata på{" "}
                      <Link to="/dokumentasjon/innrapportering">
                        dokumentasjonssiden
                      </Link>{" "}
                      og velg "Last ned CSV eksempelfil"
                    </Paragraph>
                    <Paragraph>
                      Velg regnearkprogrammet du bruker for å se veiledning for
                      hvordan du eksporterer regnearket som en CSV-fil med UTF-8
                      encoding.
                    </Paragraph>
                    <Tabs defaultValue="excel">
                      <Tabs.List>
                        <Tabs.Tab value="excel">Excel</Tabs.Tab>
                        <Tabs.Tab value="numbers">Numbers for Mac</Tabs.Tab>
                        <Tabs.Tab value="libre">LibreOffice</Tabs.Tab>
                      </Tabs.List>
                      <Tabs.Panel value="excel" className="space-y-3">
                        <Heading level={3} data-size="xs">
                          Eksporter fra Excel
                        </Heading>
                        <Paragraph>
                          Velg “Fil” og naviger ned til “Eksporter” velg “Last
                          ned som CSV UTF-8”.
                        </Paragraph>
                        <img
                          aria-hidden
                          src={EksporterExcel}
                          alt="Skjermbilde som viser hvordan man eksporterer CSV fra Excel"
                        />
                        <Paragraph>
                          <ExternalLink
                            href="https://support.microsoft.com/nb-no/office/importere-eller-eksportere-tekstfiler-txt-eller-csv-5250ac4c-663c-47ce-937b-339e391393ba"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Microsoft har en mer detaljert veiledning her
                            <ExternalLinkIcon
                              fontSize={"1.5rem"}
                              className="pl-1"
                            />
                          </ExternalLink>
                        </Paragraph>
                      </Tabs.Panel>
                      <Tabs.Panel value="numbers" className="space-y-3">
                        <Heading level={3} data-size="xs">
                          Eksporter fra Numbers (Mac)
                        </Heading>
                        <Paragraph>
                          Velg “Arkiv” og naviger ned til “Eksporter til” velg
                          “CSV...”
                        </Paragraph>
                        <img
                          aria-hidden
                          src={EksporterNumbers}
                          alt="Skjermbilde som viser hvordan man eksporterer CSV fra Numbers på Mac"
                        />
                      </Tabs.Panel>
                      <Tabs.Panel value="libre" className="space-y-3">
                        <Heading level={3} data-size="xs">
                          Eksporter fra LibreOffice
                        </Heading>
                        <Paragraph>
                          Velg “Fil” og naviger ned til “Lagre som”.
                        </Paragraph>
                        <img
                          aria-hidden
                          src={EksporterLibreOffice1}
                          alt="Skjermbilde som viser hvordan man eksporterer CSV fra LibreOffice del 1"
                        />
                        <Paragraph>
                          Under “Filtype” velg “Tekst, CSV (.csv), trykk
                          “Lagre”.
                        </Paragraph>
                        <img
                          className="w-[410px]"
                          aria-hidden
                          src={EksporterLibreOffice2}
                          alt="Skjermbilde som viser hvordan man eksporterer CSV fra LibreOffice del 2"
                        />
                      </Tabs.Panel>

                      <Tabs.Panel value="microsoft-365" className="space-y-3">
                        <Heading level={3} data-size="xs">
                          Eksporter fra Microsoft 365 Excel
                        </Heading>
                        <Paragraph>
                          Velg “Fil” og naviger ned til “Eksporter” velg “Last
                          ned som CSV UTF-8”.
                        </Paragraph>
                        <img
                          aria-hidden
                          src={EksporterMicrosoft365}
                          alt="Skjermbilde som viser hvordan man eksporterer CSV fra Microsoft 365 Excel"
                        />
                      </Tabs.Panel>
                    </Tabs>
                  </section>
                }
              />
            </section>

            <section>
              <Heading level={2} data-size="xs" className="mb-5">
                Hjelp med regneark (Excel / Number)
              </Heading>
              <InfoContainer
                title={'"Possible data loss" feilmelding i Excel'}
                content={
                  <section className="space-y-3">
                    <Paragraph>
                      Denne kan du bare ignorere. Vi bruker ingen av de mer
                      avanserte funksjonene i Excel, så CSV fungerer fint for
                      oss.
                    </Paragraph>
                  </section>
                }
              />

              <InfoContainer
                title={
                  "Excel: Når man åpner regnearket og man får rare tegn eller rar alt på en linje"
                }
                content={
                  <section className="space-y-3">
                    <Paragraph>
                      I noen tilfeller kan regnearkprogrammet åpne CSV-filen med
                      feil tegnsett (encoding). Dette kan føre til at
                      spesialtegn som æ, ø og å vises som rare symboler. I
                      tillegg kan det oppstå at alle verdier havner i en og
                      samme kolonne. Se bildet under. For å unngå dette må filen
                      åpnes på en spesiell måte.
                    </Paragraph>
                    <img
                      aria-hidden
                      src={RareVerdierImg}
                      alt="Skjermbilde som viser hvordan rare verdier kan se ut i et regneark"
                    />

                    <Paragraph>
                      Hvordan man gjør det varierer litt avhengig av hvilken
                      versjon du har. Hvis instruksene under ikke stemmer kan du
                      prøve denne fremgangsmåten:{" "}
                      <ExternalLink
                        href="https://support.microsoft.com/nb-no/office/importere-eller-eksportere-tekstfiler-txt-eller-csv-5250ac4c-663c-47ce-937b-339e391393ba"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Importer og eksporter tekstfiler (txt eller csv) i Excel
                        <ExternalLinkIcon
                          fontSize={"1.5rem"}
                          className="pl-1"
                        />
                      </ExternalLink>
                    </Paragraph>

                    <Heading level={3} data-size="xs">
                      Windows
                    </Heading>
                    <Paragraph>
                      1. Velg "Data" i menyen og deretter "Fra tekst/CSV". Velg
                      så filen du skal åpne, og pass på at
                      "Filopprinnelse"/"File Origin" er satt til "Unicode
                      (UTF-8)". Trykk så "Neste".
                    </Paragraph>

                    <img
                      aria-hidden
                      src={ImportCsvExcelWindows}
                      alt="Skjermbilde som viser hvordan man importerer CSV fra Excel å windows"
                    />

                    <Paragraph>
                      2. Deretter velger du "Skilletegn"/"Delimiters" som
                      "Komma" eller "Semikolon". Se på forhåndsvinsingen under
                      at dataen vises som den skal.
                    </Paragraph>
                    <img
                      aria-hidden
                      src={ForhåndsvisningCsv}
                      alt="Skjermbilde som viser hvordan man importerer CSV fra Excel"
                    />

                    <Heading level={3} data-size="xs">
                      Mac
                    </Heading>
                    <Paragraph>
                      1. Velg "Data" i menyen og deretter "Get Data", så
                      "Text/CSV".
                    </Paragraph>
                    <img
                      aria-hidden
                      src={ImportCsvExcelMac1}
                      alt="Skjermbilde som viser hvordan man importerer CSV fra Excel på mac"
                    />
                    <img
                      aria-hidden
                      src={ImportCsvExcelMac2}
                      alt="Skjermbilde som viser hvordan man importerer CSV fra Excel på mac"
                    />
                    <Paragraph>
                      2. Velg så filen du skal åpne og klikk neste.
                    </Paragraph>

                    <Paragraph>
                      3. Pass på at "Filopprinnelse"/"File Origin" er satt til
                      "Unicode (UTF-8)". "Skilletegn"/"Delimiters" er satt til
                      "comma/komma".
                    </Paragraph>

                    <Paragraph>
                      4. Trykk så "Neste". Se på forhåndsvinsingen under at
                      dataen vises som den skal.
                    </Paragraph>
                    <img
                      aria-hidden
                      src={ForhåndsvisningCsv}
                      alt="Skjermbilde som viser hvordan man importerer CSV fra Excel"
                    />

                    <Paragraph>
                      5. Ser alt bra, trykk "Last inn"/"Load".
                    </Paragraph>
                  </section>
                }
              />
            </section>
          </div>
        </div>
      </Page>
    </main>
  );
};

const InfoContainer = ({
  title,
  content,
}: {
  title: string;
  content: ReactElement;
}) => {
  const [tooltipMsg, setTooltipMsg] = useState("Kopier lenke");
  const id = title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  const copyLink = (event?: MouseEvent<HTMLAnchorElement>) => {
    try {
      const url = `${window.location.origin}${window.location.pathname}#${id}`;
      navigator.clipboard.writeText(url);
      setTooltipMsg("✓ Lenke kopiert!");
      event?.preventDefault();
    } catch {
      window.location.href = `#${id}`;
      setTooltipMsg(
        "Feilet å kopiere lenke, kopier manuelt fra adressefeltet.",
      );
    }
  };
  function idMatchesWindowHash(id: string): boolean | undefined {
    return window.location.hash === `#${id}`;
  }

  return (
    <Details className="mb-0" id={id} defaultOpen={idMatchesWindowHash(id)}>
      <Details.Summary>
        <span>{title}</span>
        <Tooltip content={tooltipMsg}>
          <ExternalLink
            href={`#${id}`}
            onClick={copyLink}
            aria-label={`Kopier lenke til ${title}`}
            className="ml-auto"
          >
            <LinkIcon title="Ikon av en lenke" fontSize="1.5rem" />
          </ExternalLink>
        </Tooltip>
      </Details.Summary>
      <Details.Content>{content}</Details.Content>
    </Details>
  );
};
